import { Database } from "../supabase.types";

export type ArtistValueFeedbackRow =
  Database["public"]["Tables"]["artist_value_feedback"]["Row"];

/**
 * The product moments an artist can rate, as the SINGLE SOURCE OF TRUTH. The
 * submit route derives its `surface` enum from this tuple (so accepting a new
 * surface is just adding it here), and {@link ArtistFeedbackSurface} is derived
 * from it too. Each maps to a North Star signal via the dashboard's surface
 * registry; the tuple extends without a schema change (`surface` is free text in
 * the DB). `set-up-automation` and an ads surface are deliberately not here yet
 * (the automation feature is unbuilt; ads is a separate ticket).
 */
export const ARTIST_FEEDBACK_SURFACES = [
  "publish-show",
  "post-content",
  "message-fans",
  "send-newsletter",
] as const;

/** A rateable product moment. Callers should build input with one of these. */
export type ArtistFeedbackSurface = (typeof ARTIST_FEEDBACK_SURFACES)[number];

export const PUBLISH_SHOW_SURFACE: ArtistFeedbackSurface = "publish-show";
export const POST_CONTENT_SURFACE: ArtistFeedbackSurface = "post-content";
export const MESSAGE_FANS_SURFACE: ArtistFeedbackSurface = "message-fans";
export const SEND_NEWSLETTER_SURFACE: ArtistFeedbackSurface = "send-newsletter";

/**
 * What the artist submits from the "rate-the-moment" prompt. `user_id` is NOT
 * part of this payload — the submit route sets it from the authenticated
 * session, so a caller can never attribute feedback to someone else. `surface`
 * is typed as `string` here to match the free-text DB column and the
 * zod-validated route body; use {@link ArtistFeedbackSurface} when constructing.
 */
export interface SubmitArtistValueFeedback {
  rating: number;
  surface: string;
  /** Only collected on a rating <= neutral (<= 3). */
  comment?: string | null;
  /** Attribution/debug context, e.g. { comedian_id, show_id }. */
  context?: Record<string, unknown> | null;
}
